import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, HardHat, Heart, Share2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const PUBLIC_BASE =
  process.env.REACT_APP_PUBLIC_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

const APPLAUSE_KEY = "ws_applauded_ids";

function readApplaudedSet() {
  try {
    const raw = localStorage.getItem(APPLAUSE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistApplaudedSet(set) {
  try {
    localStorage.setItem(APPLAUSE_KEY, JSON.stringify(Array.from(set)));
  } catch (err) {
    logger.warn("[story] persist failed", err);
  }
}

// Set / clear the document <head> tags so iMessage, Slack, WhatsApp, and
// any crawler that runs JS (LinkedIn, Discord) get a rich preview card.
function useOgMetaTags(submission) {
  useEffect(() => {
    if (!submission) return;
    const photoAbs = `${PUBLIC_BASE}${submission.photo_url}`;
    const pageUrl = `${PUBLIC_BASE}/story/${submission.id}`;
    const title = `“${submission.reason.slice(0, 70)}${submission.reason.length > 70 ? "…" : ""}” — ${submission.employee_name}`;
    const description = `Why I Work Safe — a story from ${submission.employee_name} on worksafestory.com`;

    const tags = [
      ["property=og:type", "article"],
      ["property=og:title", title],
      ["property=og:description", description],
      ["property=og:image", photoAbs],
      ["property=og:url", pageUrl],
      ["name=twitter:card", "summary_large_image"],
      ["name=twitter:title", title],
      ["name=twitter:description", description],
      ["name=twitter:image", photoAbs],
    ];
    const created = [];
    tags.forEach(([attr, content]) => {
      const [k, v] = attr.split("=");
      let el = document.head.querySelector(`meta[${k}="${v}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(k, v);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    });
    const originalTitle = document.title;
    document.title = `${title} · worksafestory.com`;
    return () => {
      document.title = originalTitle;
      created.forEach((el) => el.remove());
    };
  }, [submission]);
}

async function shareStory(submission) {
  const text = `“${submission.reason}” — ${submission.employee_name}`;
  const url = `${PUBLIC_BASE}/story/${submission.id}`;
  api.post(`/submissions/${submission.id}/share`).catch(() => null);
  if (navigator.share) {
    try {
      await navigator.share({ title: "Why I Work Safe", text, url });
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
      logger.warn("[story] share failed", err);
    }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Copied to clipboard.");
  } catch (err) {
    logger.warn("[story] clipboard failed", err);
  }
}

export default function Story() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [applauded, setApplauded] = useState(() => readApplaudedSet());

  useEffect(() => {
    api
      .get(`/submissions/${id}`)
      .then((r) => setSubmission(r.data))
      .catch((err) => {
        logger.warn("[story] load failed", err);
        setNotFound(true);
      });
  }, [id]);

  useOgMetaTags(submission);

  const hasApplauded = submission && applauded.has(submission.id);
  const isPublic = submission && submission.status === "approved";

  const handleApplaud = async () => {
    if (!submission || hasApplauded || !isPublic) return;
    setSubmission((prev) => ({ ...prev, applause: prev.applause + 1 }));
    const next = new Set(applauded);
    next.add(submission.id);
    setApplauded(next);
    persistApplaudedSet(next);
    try {
      const { data } = await api.post(`/submissions/${submission.id}/applause`);
      setSubmission((prev) => ({ ...prev, applause: data.applause }));
    } catch (err) {
      logger.warn("[story] applause failed", err);
      setSubmission((prev) => ({ ...prev, applause: Math.max(0, prev.applause - 1) }));
      const rolled = new Set(applauded);
      rolled.delete(submission.id);
      setApplauded(rolled);
      persistApplaudedSet(rolled);
    }
  };

  if (notFound) {
    return (
      <div className="bg-white min-h-screen text-[#09090B]">
        <SiteHeader />
        <section className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display font-black text-4xl tracking-tighter">Story not found.</h1>
          <p className="mt-3 text-[#52525B]">It may have been removed by the safety team.</p>
          <Link
            to="/wall"
            className="inline-flex items-center gap-2 mt-8 bg-[#09090B] text-white font-bold px-6 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow hover:bg-[#FF5A00] transition-colors"
          >
            <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Visit the Wall
          </Link>
        </section>
        <SiteFooter />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="bg-white min-h-screen text-[#09090B]">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center ws-label">Loading story…</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen text-[#09090B]" data-testid="story-page">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-2 gap-10">
          <div className="border-2 border-[#09090B] bg-[#F4F4F5] overflow-hidden ws-hard-shadow">
            <img
              src={submission.photo_url}
              alt={submission.employee_name}
              className="w-full h-full object-cover aspect-[4/5]"
            />
          </div>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => navigate(-1)}
              data-testid="story-back-btn"
              className="ws-label text-[#52525B] hover:text-[#FF5A00] inline-flex items-center gap-2 self-start"
            >
              <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Back
            </button>
            <div className="ws-label text-[#FF5A00] mt-6">/ A story from the floor</div>
            <blockquote className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 leading-[1.05]">
              “{submission.reason}”
            </blockquote>
            <div className="mt-6 flex items-center gap-2 text-base text-[#09090B]">
              <HardHat strokeWidth={2.5} className="w-5 h-5" />
              <span className="font-display font-bold">{submission.employee_name}</span>
              {submission.approved_at && (
                <span className="text-sm text-[#52525B]">
                  · {new Date(submission.approved_at).toLocaleDateString()}
                </span>
              )}
            </div>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleApplaud}
                disabled={hasApplauded || !isPublic}
                data-testid="story-applaud-btn"
                className={`inline-flex items-center gap-2 px-5 py-3 border-2 border-[#09090B] ws-label transition-colors disabled:cursor-default ${
                  hasApplauded ? "bg-[#FF5A00] text-white" : "bg-white text-[#09090B] hover:bg-[#FFCC00]"
                }`}
              >
                <Heart strokeWidth={2.5} className={`w-4 h-4 ${hasApplauded ? "fill-white" : ""}`} />
                <span>{submission.applause} applause</span>
              </button>
              <button
                type="button"
                onClick={() => shareStory(submission)}
                data-testid="story-share-btn"
                className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#09090B] bg-white text-[#09090B] hover:bg-[#FFCC00] ws-label transition-colors"
              >
                <Share2 strokeWidth={2.5} className="w-4 h-4" /> Share
              </button>
            </div>

            {!isPublic && (
              <div className="mt-6 border-2 border-[#FFCC00] bg-[#FFFBEA] px-4 py-3 text-sm">
                This story is awaiting approval — it will appear on the public Wall once a reviewer
                signs off. The link is private until then.
              </div>
            )}

            <div className="mt-auto pt-10 flex items-center gap-3">
              <Link
                to="/submit"
                data-testid="story-submit-cta"
                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-bold px-5 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow-sm"
              >
                Share my own story <ArrowRight strokeWidth={2.5} className="w-4 h-4" />
              </Link>
              <Link
                to="/wall"
                data-testid="story-wall-link"
                className="ws-label text-[#09090B] underline hover:text-[#FF5A00]"
              >
                Visit the wall
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
